import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { X, Pencil, RotateCcw, AlertTriangle, Bot, Unlock, Pin, PinOff, XCircle, ArrowRightToLine, ListX, ChevronDown, LayoutPanelLeft } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useWorkspaceStore } from '../store/workspaceStore';
import { WORKSPACE_CLOSE_ACTIVE_TAB_EVENT } from '../workspaceEvents';
import { useSessionStore } from '../../../sessions/store/sessionStore';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { useAiLockStore } from '../../../terminal/store/aiLockStore';
import { useLockDuration } from '../../../terminal/hooks/useLockDuration';
import { useWorkspacePinState } from '../../common/hooks/useWorkspacePinState';
import { useWorkspacePinActions } from '../../common/hooks/useWorkspacePinActions';
import { useAppStore } from '../../../../store/appStore';
import type { Tab } from '../types/workspaceTypes';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '../../../../components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../../components/ui/tooltip';
import { generateQueryName, deduplicateName, renumberDuplicateNames } from '../../query-editor/utils/queryNameGenerator';
import { getFileNameFromPath, isFileOutOfDate } from '../../query-editor/utils/queryFileUtils';
import { isQueryTab, isNotebookTab, isPlSqlEditorTab } from '../types/workspaceTypes';
import { getTabIcon } from '../utils/tabIcon';
import type { TransactionMode, TransactionState } from '../types/workspaceTypes';
import { TransactionCloseDialog } from '../../query-editor/components/TransactionCloseDialog';
import { NotebookCloseDialog } from '../../notebook/components/NotebookCloseDialog';
import { ImportCloseDialog } from '../../data-import/ImportCloseDialog';
import { useDataImportStore } from '../../data-import/store/dataImportStore';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { completeTabsClose, requestTabsClose, useTabCloseGuardStore } from '../store/tabCloseGuardStore';
import { serializeToIpynb } from '../../notebook/utils/ipynbSerializer';
import { saveNotebookFile } from '../../notebook/services/notebookFileApi';
import { closeStorageTab } from '../../storage-explorer/preview/storagePreviewOpenClose';

interface TabsProps {
  className?: string;
}

/**
 * Check if a connection is disconnected based on session state
 */
function useIsConnectionDisconnected(connectionId: string | undefined): boolean {
  const currentSession = useSessionStore((state) => state.currentSession);
  if (!connectionId || !currentSession) return false;

  const sessionConnection = currentSession.connections.find(
    (c) => c.connectionId === connectionId
  );
  return sessionConnection?.status === 'disconnected';
}

/**
 * Look up a connection's color from the connection store
 */
function useConnectionColor(connectionId: string | undefined): string | undefined {
  return useConnectionStore(
    (state) => connectionId ? state.connections.find((c) => c.id === connectionId)?.color : undefined
  );
}

// PP-09 adaptive tab-title width. The ceiling (320px) is what a title gets
// with few tabs open; as more tabs compete for the strip's width the cap
// tightens toward the 240px floor. Dividing the strip's OWN measured width by
// the tab count is a deliberately simple proxy for "the strip is filling" —
// CSS flex-wrap's shrink step only resolves AFTER items are already assigned
// to a line (it cannot pull more items onto a line by shrinking them first),
// so a pure-CSS flex-basis trick can't produce this behavior; a ResizeObserver
// reading real layout is the direct way to get it. This only ever caps a
// title — `width` stays `auto`, so a short title is never padded out to fill
// the cap (verified in core/tabs.spec.ts).
const MIN_TAB_TITLE_WIDTH_PX = 240;
const MAX_TAB_TITLE_WIDTH_PX = 320;
// Icon + gaps + indicator dots + close button, reserved out of the per-tab
// budget before the title gets what's left.
const TAB_CHROME_RESERVED_PX = 90;

/**
 * Measures the (horizontal) tab strip's width and returns a ref to attach to
 * it plus the current per-tab title cap. Returns the ceiling before the first
 * measurement lands, so a title is never briefly unbounded on first paint.
 *
 * Uses a CALLBACK ref rather than `useRef` + `useEffect([])`: `Tabs()` early-
 * returns `null` while `tabs.length === 0` (e.g. before the Home tab exists),
 * so the strip's `<div>` may not exist yet on this component's FIRST commit.
 * A `useEffect([])` runs exactly once, tied to that first commit — if the
 * ref was still null then, the observer never attaches and never gets a
 * second chance once the div does mount on a later render. A callback ref
 * fires fresh on every attach, so it can't miss a later mount.
 */
function useAdaptiveTabTitleWidth(
  tabCount: number,
  // T6.1c (item 4) — vertical mode discards `titleMaxWidthPx` entirely (see
  // the call site), so observing there spent a ResizeObserver callback plus a
  // `setContainerWidth` re-render on every sidebar resize to produce a number
  // nothing read. No visible bug; purely wasted renders. Gate the OBSERVER,
  // not the returned value — gating only the value would leave the re-render
  // loop running, which is the thing that costs anything.
  enabled: boolean,
): [(node: HTMLDivElement | null) => void, number] {
  const [containerWidth, setContainerWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!enabled || !node || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (typeof width === 'number') setContainerWidth(width);
    });
    ro.observe(node);
    observerRef.current = ro;
    // `enabled` MUST stay in the deps: the callback ref's identity has to
    // change when orientation flips, or React never re-runs it and a strip
    // mounted vertical would stay unobserved after switching to horizontal.
  }, [enabled]);

  const titleMaxWidthPx =
    containerWidth === 0 || tabCount === 0
      ? MAX_TAB_TITLE_WIDTH_PX
      : Math.min(
          MAX_TAB_TITLE_WIDTH_PX,
          Math.max(MIN_TAB_TITLE_WIDTH_PX, containerWidth / tabCount - TAB_CHROME_RESERVED_PX)
        );

  return [containerRef, titleMaxWidthPx];
}

// PP-09 connection bar: 3px→2px so the active and inactive states share one
// width, differentiated only by opacity (a calmer, thinner accent than a
// width jump). "Slightly transparent" for inactive is ~90% opacity (the `e6`
// hex-alpha suffix) — a deliberately subtle dim, not the ~40% fade ("66") the
// bar used before this task, which read as more than "slight". Shared by
// TabItem and the drag-overlay TabItemContent so both respect `isActive`
// (the drag overlay previously always rendered the active/3px treatment).
const CONNECTION_BAR_INACTIVE_ALPHA_HEX = 'e6';

function connectionBarStyle(
  connectionColor: string | undefined,
  isActive: boolean,
  isVertical: boolean
): React.CSSProperties | undefined {
  if (!connectionColor) return undefined;
  const color = isActive ? connectionColor : `${connectionColor}${CONNECTION_BAR_INACTIVE_ALPHA_HEX}`;
  return isVertical
    ? { borderLeftWidth: '2px', borderLeftColor: color, borderLeftStyle: 'solid' }
    : { borderBottomWidth: '2px', borderBottomColor: color, borderBottomStyle: 'solid' };
}

export function Tabs({ className = '' }: TabsProps) {
  const { tabs, activeTabId, setActiveTab, removeTab, updateTabTitle, resetTabTitle, getTabTitles, batchUpdateTabTitles, pinTab, unpinTab, closeOtherTabs, closeTabsToRight, closeUnpinnedTabs, reorderTabs, moveTab } =
    useWorkspaceStore();

  const tabOrientation = useAppStore((state) => state.tabOrientation);
  const setTabOrientation = useAppStore((state) => state.setTabOrientation);
  const { track } = useAnalytics();

  const isVertical = tabOrientation === 'vertical';
  const sortingStrategy = isVertical ? verticalListSortingStrategy : horizontalListSortingStrategy;

  // Track which tab is being dragged
  const [activeId, setActiveId] = useState<string | null>(null);

  // dnd-kit sensors for pointer and keyboard
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Only start dragging after 8px movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get tab IDs for SortableContext
  const startPageTab = useMemo(
    () => tabs.find((tab) => tab.type === 'start-page') ?? null,
    [tabs]
  );

  const sortableTabs = useMemo(
    () => tabs.filter((tab) => tab.type !== 'start-page' && !tab.id.includes('::pkg::')),
    [tabs]
  );

  const tabIds = useMemo(() => sortableTabs.map((tab) => tab.id), [sortableTabs]);

  // PP-09 — adaptive title cap. Vertical mode keeps its existing `flex-1`
  // sizing (a narrow sidebar column, not a wrapping row under width pressure
  // from tab count), so the measured cap only applies horizontally.
  // T6.1c (item 4) — `!isVertical` also switches the observer off, so vertical
  // mode no longer measures a cap it then throws away on the next line.
  const [tabsListRef, adaptiveTitleMaxWidthPx] = useAdaptiveTabTitleWidth(
    sortableTabs.length,
    !isVertical,
  );
  const titleMaxWidthPx = isVertical ? undefined : adaptiveTitleMaxWidthPx;

  // Find the tab being dragged for the overlay
  const activeTab = activeId ? sortableTabs.find((tab) => tab.id === activeId) : null;

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || active.id === over.id) return;

      const fromIndex = tabs.findIndex((tab) => tab.id === active.id);
      const toIndex = tabs.findIndex((tab) => tab.id === over.id);

      if (fromIndex === -1 || toIndex === -1) return;

      // Enforce pinned/unpinned group boundaries
      const movingTab = tabs[fromIndex];
      const targetTab = tabs[toIndex];

      // Pinned tabs can only be reordered among other pinned tabs
      // Unpinned tabs can only be reordered among other unpinned tabs
      if (movingTab.isPinned !== targetTab.isPinned) {
        return; // Don't allow moving between groups
      }

      reorderTabs(fromIndex, toIndex);
    },
    [tabs, reorderTabs]
  );

  // Transaction close dialog: every tab-removal path asks through
  // `requestTabsClose` (tabCloseGuardStore.ts), this dialog answers.
  const transactionCloseRequest = useTabCloseGuardStore((state) => state.request);
  const [isTransactionProcessing, setIsTransactionProcessing] = useState(false);

  /** Check for active transaction, unsaved notebook, or a live import before closing; show dialog if needed */
  const handleCloseTab = useCallback((tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId);
    // An open transaction, or one still starting: ask first (and end it).
    if (tab && isQueryTab(tab)) {
      requestTabsClose([tabId], (kept) => { if (!kept.has(tabId)) removeTab(tabId); });
      return;
    }
    if (tab && isNotebookTab(tab) && tab.content.isDirty) {
      setNotebookCloseTabId(tabId);
      return;
    }
    // A file edited in a storage tab's preview pane and not saved yet: ask before the edit goes.
    if (tab && tab.type === 'storage-explorer') {
      closeStorageTab(tabId, tab.content.mode, removeTab);
      return;
    }
    // Finding #2: closing a wizard tab mid-import would silently detach the live run.
    if (tab && tab.type === 'data-import' && useDataImportStore.getState().getActiveRun(tabId)) {
      setImportCloseTabId(tabId);
      return;
    }
    removeTab(tabId);
  }, [tabs, removeTab]);

  // The native application menu's "Close Tab" (Cmd/Ctrl+W) lands here rather
  // than calling `removeTab` itself, so the chord and the tab's X button share
  // ONE guarded path — an open transaction, a dirty notebook or a live import
  // prompts either way instead of silently vanishing (and leaking server-side
  // locks). Mirrors the `jam:query-editor:toggle-results-pane` bridge.
  useEffect(() => {
    const onCloseActiveTab = () => {
      const { activeTabId: currentActiveTabId } = useWorkspaceStore.getState();
      if (currentActiveTabId) handleCloseTab(currentActiveTabId);
    };
    window.addEventListener(WORKSPACE_CLOSE_ACTIVE_TAB_EVENT, onCloseActiveTab);
    return () => window.removeEventListener(WORKSPACE_CLOSE_ACTIVE_TAB_EVENT, onCloseActiveTab);
  }, [handleCloseTab]);

  const endTransactionsAndClose = useCallback(async (action: 'commit' | 'rollback') => {
    const request = useTabCloseGuardStore.getState().request;
    if (!request) return;
    setIsTransactionProcessing(true);
    try {
      await completeTabsClose(request, action);
      useTabCloseGuardStore.getState().clear();
    } finally {
      setIsTransactionProcessing(false);
    }
  }, []);
  const handleTransactionCommitAndClose = useCallback(() => endTransactionsAndClose('commit'), [endTransactionsAndClose]);
  const handleTransactionRollbackAndClose = useCallback(() => endTransactionsAndClose('rollback'), [endTransactionsAndClose]);

  const handleTransactionCloseCancel = useCallback(() => {
    useTabCloseGuardStore.getState().clear();
  }, []);

  // Notebook unsaved changes close dialog state
  const [notebookCloseTabId, setNotebookCloseTabId] = useState<string | null>(null);
  const [isNotebookSaving, setIsNotebookSaving] = useState(false);

  const handleNotebookSaveAndClose = useCallback(async () => {
    if (!notebookCloseTabId) return;
    setIsNotebookSaving(true);
    try {
      const tab = tabs.find((t) => t.id === notebookCloseTabId);
      if (!tab || !isNotebookTab(tab)) { removeTab(notebookCloseTabId); setNotebookCloseTabId(null); return; }

      const saveMode = tab.content.metadata.saveMode ?? 'rich';
      const ipynbJson = serializeToIpynb(tab.content, saveMode);
      const result = await saveNotebookFile({
        content: ipynbJson,
        filePath: tab.content.filePath,
        suggestedFileName: tab.title,
      });

      if (result.success) {
        removeTab(notebookCloseTabId);
        setNotebookCloseTabId(null);
      } else if ('cancelled' in result && result.cancelled) {
        // User cancelled save dialog — stay on the close dialog
      }
    } finally {
      setIsNotebookSaving(false);
    }
  }, [notebookCloseTabId, tabs, removeTab]);

  const handleNotebookDiscardAndClose = useCallback(() => {
    if (!notebookCloseTabId) return;
    removeTab(notebookCloseTabId);
    setNotebookCloseTabId(null);
  }, [notebookCloseTabId, removeTab]);

  const handleNotebookCloseCancel = useCallback(() => {
    setNotebookCloseTabId(null);
  }, []);

  // Data Import live-run close dialog state (finding #2)
  const [importCloseTabId, setImportCloseTabId] = useState<string | null>(null);

  const handleImportCancelAndClose = useCallback(() => {
    if (!importCloseTabId) return;
    // Cancel via the existing IPC (through the stored operationId), then close — never orphan the run.
    const activeRun = useDataImportStore.getState().getActiveRun(importCloseTabId);
    if (activeRun) {
      void window.electronAPI?.dataImport?.cancel(activeRun.operationId);
      useDataImportStore.getState().markRunSettled(importCloseTabId);
      track('feature:data_import_close_cancel');
    }
    removeTab(importCloseTabId);
    setImportCloseTabId(null);
  }, [importCloseTabId, removeTab, track]);

  const handleImportCloseKeepOpen = useCallback(() => {
    setImportCloseTabId(null);
  }, []);

  // Track which tab is being edited
  const [editingTabId, setEditingTabId] = useState<string | null>(null);

  // All hooks must be called before any conditional returns
  const handleStartEditing = useCallback((tabId: string) => {
    setEditingTabId(tabId);
  }, []);

  const handleStopEditing = useCallback(() => {
    setEditingTabId(null);
  }, []);

  const handleSaveTitle = useCallback(
    (tabId: string, newTitle: string) => {
      const trimmedTitle = newTitle.trim();
      if (trimmedTitle) {
        // Deduplicate the title
        const existingTitles = getTabTitles().filter(
          (_, i) => tabs[i]?.id !== tabId
        );
        const uniqueTitle = deduplicateName(trimmedTitle, existingTitles);
        updateTabTitle(tabId, uniqueTitle, true);
      }
      handleStopEditing();
    },
    [getTabTitles, tabs, updateTabTitle, handleStopEditing]
  );

  const handleResetToAuto = useCallback(
    (tabId: string) => {
      const tab = tabs.find((t) => t.id === tabId);
      if (tab && isQueryTab(tab)) {
        const query = tab.content.query || '';
        const autoName = generateQueryName(query) || `Query-${tabId}`;
        const existingTitles = getTabTitles().filter((_, i) => tabs[i]?.id !== tabId);
        const uniqueTitle = deduplicateName(autoName, existingTitles);
        resetTabTitle(tabId, uniqueTitle);

        // Renumber all tabs with the same base name if needed
        setTimeout(() => {
          const freshTabs = useWorkspaceStore.getState().tabs;
          const renameMap = renumberDuplicateNames(autoName, freshTabs);
          if (renameMap) {
            batchUpdateTabTitles(renameMap);
          }
        }, 0);
      }
    },
    [tabs, getTabTitles, resetTabTitle, batchUpdateTabTitles]
  );

  const removeLock = useAiLockStore((s) => s.removeLock);
  const setAbortRequested = useAiLockStore((s) => s.setAbortRequested);

  const handleForceUnlock = useCallback(
    (tabId: string) => {
      // Update the local store to release the lock
      // The per-tab meta lock system manages file cleanup when the AI session ends
      removeLock(tabId);
    },
    [removeLock]
  );

  const handleAbortEdit = useCallback(
    async (tabId: string) => {
      // Get the lock info to find the session ID
      const lockInfo = useAiLockStore.getState().lockedTabs.get(tabId);
      if (!lockInfo) return;

      try {
        // Request abort via the meta file
        await window.electronAPI?.aiWorkspace?.writeAbortRequest(lockInfo.sessionId, tabId);
        // Update local state to show abort was requested
        setAbortRequested(tabId, true);
      } catch (err) {
        console.error('[Tabs] Error requesting abort:', err);
      }
    },
    [setAbortRequested]
  );

  const handlePinTab = useCallback(
    (tabId: string) => {
      pinTab(tabId);
    },
    [pinTab]
  );

  const handleUnpinTab = useCallback(
    (tabId: string) => {
      unpinTab(tabId);
    },
    [unpinTab]
  );

  // The bulk closes remove exactly what the store actions remove, after the
  // same transaction guard as a single close (`requestTabsClose`).
  const handleCloseOtherTabs = useCallback(
    (tabId: string) => {
      const closing = tabs.filter((tab) => tab.id !== tabId && !tab.isPinned && tab.type !== 'start-page');
      requestTabsClose(closing.map((tab) => tab.id), (kept) => closeOtherTabs(tabId, kept));
    },
    [tabs, closeOtherTabs]
  );

  const handleCloseTabsToRight = useCallback(
    (tabId: string) => {
      const index = tabs.findIndex((tab) => tab.id === tabId);
      if (index === -1) return;
      const closing = tabs.slice(index + 1).filter((tab) => !tab.isPinned && tab.type !== 'start-page');
      requestTabsClose(closing.map((tab) => tab.id), (kept) => closeTabsToRight(tabId, kept));
    },
    [tabs, closeTabsToRight]
  );

  const handleCloseUnpinnedTabs = useCallback(() => {
    const closing = tabs.filter((tab) => !tab.isPinned && tab.type !== 'start-page');
    requestTabsClose(closing.map((tab) => tab.id), (kept) => closeUnpinnedTabs(kept));
  }, [tabs, closeUnpinnedTabs]);

  const handleMoveTab = useCallback(
    (tabId: string, direction: 'left' | 'right') => {
      moveTab(tabId, direction);
    },
    [moveTab]
  );

  // Compute whether there are other closable tabs (not pinned)
  const hasOtherClosableTabs = useCallback(
    (tabId: string) => {
      return tabs.some((tab) => tab.id !== tabId && !tab.isPinned && tab.type !== 'start-page');
    },
    [tabs]
  );

  // Compute whether there are closable tabs to the right (not pinned)
  const hasClosableTabsToRight = useCallback(
    (tabId: string) => {
      const tabIndex = tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex === -1) return false;
      return tabs
        .slice(tabIndex + 1)
        .some((tab) => !tab.isPinned && tab.type !== 'start-page');
    },
    [tabs]
  );

  // Compute whether there are any unpinned tabs to close
  const hasUnpinnedTabs = tabs.some((tab) => !tab.isPinned && tab.type !== 'start-page');

  // Early return AFTER all hooks are called
  if (tabs.length === 0) {
    return null;
  }

  // GATE-1 finding F5#1 (chrome review): at rest — only the Home tab open —
  // this strip was a 29px band of `bg-background` sandwiched between two
  // `--chrome`-colored hairlines (the title bar above, the workspace canvas
  // below), reading as a dead gap rather than intentional framing. Fixed by
  // giving the strip the SAME `bg-chrome`/`border-divider` pairing MainToolbar
  // and FilterWizard's filter row (PP-11) already use for "this reads as
  // chrome, not canvas" — see the container div below. That also gives the
  // *active* tab's `bg-background` more contrast against its surroundings,
  // which is the native tab-strip convention (active tab = canvas color,
  // inactive tabs + the strip itself = chrome). Collapsing the strip entirely
  // at rest was considered and rejected: the Home tab and the orientation
  // menu need to live somewhere, and relocating them into the title bar is a
  // cross-file change outside a tab-visuals task.
  //
  // GATE-1 finding F5#2: the title-bar "Home" button (MainToolbar.tsx,
  // `data-testid="home-button"`) is always `bg-primary`-filled regardless of
  // which tab is active. That element lives outside this task's file scope
  // (Tabs.tsx only) — CONSTRAINTS.md forbids touching unrelated files, and
  // MainToolbar.tsx is T2.2's (CH-02) file. Ruling: stays as-is. It is not a
  // hardcoded-color violation — `bg-primary` is an accepted "primary button"
  // role per the accent system — and it reads as a persistent CTA ("click to
  // go home"), not a tab-state indicator, so it not tracking the active tab
  // is a defensible, if debatable, design rather than a bug. Resolving the
  // "two Home affordances" overlap GATE-1 flagged would need a coordinated
  // redesign spanning MainToolbar.tsx and Tabs.tsx together; that's a
  // follow-up for whoever owns both, not a unilateral change here.
  const renderTrailingCluster = (stretchHome: boolean) => (
    <>
      {startPageTab && (
        <TabItem
          tab={startPageTab}
          isActive={startPageTab.id === activeTabId}
          isEditing={false}
          titleMaxWidthPx={titleMaxWidthPx}
          onActivate={() => setActiveTab(startPageTab.id)}
          onClose={() => handleCloseTab(startPageTab.id)}
          onStartEditing={() => handleStartEditing(startPageTab.id)}
          onStopEditing={handleStopEditing}
          onSaveTitle={(title) => handleSaveTitle(startPageTab.id, title)}
          onResetToAuto={() => handleResetToAuto(startPageTab.id)}
          onForceUnlock={() => handleForceUnlock(startPageTab.id)}
          onAbortEdit={() => handleAbortEdit(startPageTab.id)}
          onPinTab={() => handlePinTab(startPageTab.id)}
          onUnpinTab={() => handleUnpinTab(startPageTab.id)}
          onCloseOtherTabs={() => handleCloseOtherTabs(startPageTab.id)}
          canCloseOtherTabs={hasOtherClosableTabs(startPageTab.id)}
          onCloseTabsToRight={() => handleCloseTabsToRight(startPageTab.id)}
          canCloseTabsToRight={hasClosableTabsToRight(startPageTab.id)}
          onCloseUnpinnedTabs={handleCloseUnpinnedTabs}
          canCloseUnpinnedTabs={hasUnpinnedTabs}
          onMoveTab={(direction) => handleMoveTab(startPageTab.id, direction)}
          className={stretchHome ? 'flex-1' : undefined}
        />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            // T6.1c (item 3) — was `focus:ring-2 focus:ring-primary/50`, the
            // app's only remaining `focus:` (not `focus-visible:`) ring, and
            // the only one pointing at `--primary` instead of `--ring`.
            // Consistency does justify touching it: `--ring` is the token that
            // MEANS focus, and `focus:` paints a ring after a mouse click,
            // which no other button in the app does. The hue does not move —
            // `--primary` and `--ring` are the same hex in both themes — so
            // this is a behaviour and vocabulary fix, not a restyle.
            className={`p-0.5 ${isVertical ? 'mr-3' : 'mr-1'} rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background`}
            aria-label="Tab bar menu"
            data-testid="tab-bar-menu-trigger"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" data-testid="tab-bar-menu">
          <DropdownMenuItem
            onClick={() =>
              setTabOrientation(tabOrientation === 'horizontal' ? 'vertical' : 'horizontal')
            }
            data-testid="toggle-tab-orientation"
          >
            <LayoutPanelLeft className="mr-2 h-3.5 w-3.5" />
            <span>
              {tabOrientation === 'horizontal' ? 'Switch to vertical tabs' : 'Switch to horizontal tabs'}
            </span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
	      <div
	        className={
	          isVertical
	            ? `flex flex-col bg-chrome h-full ${className}`
	            : `flex items-start bg-chrome border-b border-divider ${className}`
	        }
	        data-testid="tabs-container"
	        data-orientation={isVertical ? 'vertical' : 'horizontal'}
	      >
	        <div
	          data-testid="workspace-tabs"
	          className={isVertical ? 'flex-1 min-h-0 flex flex-col overflow-y-auto' : 'w-full'}
	        >
	          <div
	            ref={tabsListRef}
	            className={
	              isVertical
	                ? 'flex flex-col gap-0.5 items-stretch text-xs w-full'
	                : 'flex flex-wrap gap-0.5 items-stretch text-xs w-full'
	            }
	            role="tablist"
	            aria-label="Open tabs"
	            // CH-18 (T4.7): matches which arrow-key axis handleTabKeyDown
	            // actually binds (Left/Right horizontal, Up/Down vertical) —
	            // see the design note above handleTabKeyDown for why the
	            // strip's multi-row wrap in horizontal mode doesn't change this.
	            aria-orientation={isVertical ? 'vertical' : 'horizontal'}
	            data-testid="tabs-list"
	          >
	            <SortableContext items={tabIds} strategy={sortingStrategy}>
	              {sortableTabs.map((tab) => (
	                <SortableTabItem
	                  key={tab.id}
	                  tab={tab}
	                  isActive={tab.id === activeTabId}
	                  isEditing={tab.id === editingTabId}
	                  isDragging={tab.id === activeId}
	                  titleMaxWidthPx={titleMaxWidthPx}
	                  onActivate={() => setActiveTab(tab.id)}
	                  onClose={() => handleCloseTab(tab.id)}
	                  onStartEditing={() => handleStartEditing(tab.id)}
	                  onStopEditing={handleStopEditing}
	                  onSaveTitle={(title) => handleSaveTitle(tab.id, title)}
	                  onResetToAuto={() => handleResetToAuto(tab.id)}
	                  onForceUnlock={() => handleForceUnlock(tab.id)}
	                  onAbortEdit={() => handleAbortEdit(tab.id)}
	                  onPinTab={() => handlePinTab(tab.id)}
	                  onUnpinTab={() => handleUnpinTab(tab.id)}
	                  onCloseOtherTabs={() => handleCloseOtherTabs(tab.id)}
	                  canCloseOtherTabs={hasOtherClosableTabs(tab.id)}
	                  onCloseTabsToRight={() => handleCloseTabsToRight(tab.id)}
	                  canCloseTabsToRight={hasClosableTabsToRight(tab.id)}
	                  onCloseUnpinnedTabs={handleCloseUnpinnedTabs}
	                  canCloseUnpinnedTabs={hasUnpinnedTabs}
	                  onMoveTab={(direction) => handleMoveTab(tab.id, direction)}
	                />
	              ))}
	            </SortableContext>

	            {!isVertical && (
	              <div className="ml-auto flex items-center" data-testid="tab-bar-trailing">
	                {renderTrailingCluster(false)}
	              </div>
	            )}
	          </div>
	        </div>
	        {isVertical && (
	          <div
	            className="flex items-center text-xs border-t border-border mt-auto"
	            data-testid="tab-bar-trailing"
	          >
	            {renderTrailingCluster(true)}
	          </div>
	        )}
	      </div>
      <DragOverlay>
        {activeTab ? (
          <TabItemContent
            tab={activeTab}
            isActive={activeTab.id === activeTabId}
            isEditing={false}
            isDragOverlay
          />
        ) : null}
      </DragOverlay>
      <TransactionCloseDialog
        open={transactionCloseRequest !== null}
        onCommitAndClose={handleTransactionCommitAndClose}
        onRollbackAndClose={handleTransactionRollbackAndClose}
        onCancel={handleTransactionCloseCancel}
        isProcessing={isTransactionProcessing}
        tabCount={transactionCloseRequest?.tabIds.length ?? 1}
        startingOnly={transactionCloseRequest?.startingOnly ?? false}
      />
      <NotebookCloseDialog
        open={notebookCloseTabId !== null}
        onSaveAndClose={handleNotebookSaveAndClose}
        onDiscardAndClose={handleNotebookDiscardAndClose}
        onCancel={handleNotebookCloseCancel}
        isSaving={isNotebookSaving}
      />
      <ImportCloseDialog
        open={importCloseTabId !== null}
        onCancelRunAndClose={handleImportCancelAndClose}
        onKeepOpen={handleImportCloseKeepOpen}
      />
    </DndContext>
  );
}

interface TabItemProps {
  tab: Tab;
  isActive: boolean;
  isEditing: boolean;
  isDragging?: boolean;
  /** PP-09 adaptive title cap in px (horizontal mode only; see useAdaptiveTabTitleWidth). */
  titleMaxWidthPx?: number;
  onActivate: () => void;
  onClose: () => void;
  onStartEditing: () => void;
  onStopEditing: () => void;
  onSaveTitle: (title: string) => void;
  onResetToAuto: () => void;
  onForceUnlock: () => void;
  onAbortEdit: () => void;
  onPinTab: () => void;
  onUnpinTab: () => void;
  onCloseOtherTabs: () => void;
  canCloseOtherTabs: boolean;
  onCloseTabsToRight: () => void;
  canCloseTabsToRight: boolean;
  onCloseUnpinnedTabs: () => void;
  canCloseUnpinnedTabs: boolean;
  onMoveTab: (direction: 'left' | 'right') => void;
  className?: string;
}

/**
 * Props for the visual tab content (used by both SortableTabItem and DragOverlay)
 */
interface TabItemContentProps {
  tab: Tab;
  isActive: boolean;
  isEditing: boolean;
  isDragOverlay?: boolean;
  editValue?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onEditValueChange?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: () => void;
  onActivate?: () => void;
  onClose?: () => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
}

/**
 * TabItemContent - Simplified visual content for drag overlay
 */
function TabItemContent({
  tab,
  isActive,
  isDragOverlay,
}: TabItemContentProps) {
  const isVertical = useAppStore((state) => state.tabOrientation === 'vertical');
  const filePath = isQueryTab(tab) ? tab.content.filePath : undefined;
  const fileName = filePath ? getFileNameFromPath(filePath) : '';
  const baseTitle = filePath ? (fileName || tab.title) : tab.title;
  const connectionColor = useConnectionColor(tab.connectionInfo?.connectionId);
  const TabTypeIcon = getTabIcon(tab);

  return (
    <div
      className={`
        flex items-center px-3 py-1.5 cursor-grabbing select-none shadow-lg
        ${isDragOverlay ? 'bg-background border border-primary/50 rounded-md' : ''}
        ${
          isActive
            ? 'bg-background text-foreground'
            : 'bg-muted text-muted-foreground'
        }
      `}
      style={connectionBarStyle(connectionColor, isActive, isVertical)}
    >
      {/* Tab type icon */}
      <TabTypeIcon className="h-3.5 w-3.5 flex-shrink-0 mr-1.5 text-primary/70" />
      <span className={`truncate min-w-0 text-xs ${isVertical ? 'flex-1' : 'max-w-32'}`}>
        {baseTitle}
      </span>
      {tab.isPinned && (
        <Pin className="h-3 w-3 text-blue-500 flex-shrink-0 ml-1" />
      )}
      <X className="ml-1.5 h-3 w-3 opacity-70 flex-shrink-0" />
    </div>
  );
}

/**
 * SortableTabItem - Wrapper that adds drag-drop functionality using dnd-kit
 */
function SortableTabItem(props: TabItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: props.tab.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TabItem
      {...props}
      sortableProps={{
        ref: setNodeRef,
        style,
        attributes,
        listeners,
        isDragging: isSortableDragging || (props.isDragging ?? false),
      }}
    />
  );
}

/**
 * Props for sortable functionality passed from SortableTabItem to TabItem
 */
interface SortableProps {
  ref: (node: HTMLElement | null) => void;
  style: React.CSSProperties;
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
  isDragging: boolean;
}

export function TabItem({
  tab,
  isActive,
  isEditing,
  titleMaxWidthPx,
  onActivate,
  onClose,
  onStartEditing,
  onStopEditing,
  onSaveTitle,
  onResetToAuto,
  onForceUnlock,
  onAbortEdit,
  onPinTab,
  onUnpinTab,
  onCloseOtherTabs,
  canCloseOtherTabs,
  onCloseTabsToRight,
  canCloseTabsToRight,
  onCloseUnpinnedTabs,
  canCloseUnpinnedTabs,
  onMoveTab,
  className: extraClassName,
  sortableProps,
}: TabItemProps & { sortableProps?: SortableProps }) {
  const isStartPage = tab.type === 'start-page';
  const [editValue, setEditValue] = useState(tab.title);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const allowDropdownOpenRef = useRef(false);
  // CH-18 (T4.7) fix round 1, Finding 1 — Radix's context/dropdown menus both
  // default to `onCloseAutoFocus` returning focus to their `asChild` trigger
  // (the tab div itself) once the menu closes. That fires AFTER the Rename
  // item's onClick has already set `isEditing`, so it stomps the rename
  // input's own `inputRef.current.focus()` (the effect below) a tick later —
  // a pre-existing bug, never caught before because the only prior rename
  // test (right-click path) asserted the input was visible, not focused. Set
  // to true only by the Rename item's onClick, consumed by exactly one
  // `onCloseAutoFocus` below, so every other menu action's close-focus
  // behavior (which the previous review explicitly routed away from this
  // task) is untouched.
  const suppressMenuCloseAutoFocusRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isDisconnected = useIsConnectionDisconnected(tab.connectionInfo?.connectionId);
  const connectionColor = useConnectionColor(tab.connectionInfo?.connectionId);
  const isAiLocked = useAiLockStore((s) => s.isLocked(tab.id));
  const lockInfo = useAiLockStore((s) => s.getLockInfo(tab.id));
  const lockTimeoutMinutes = useAppStore((s) => s.aiTerminalSettings.aiWorkspace.lockTimeoutMinutes);
  const isVertical = useAppStore((s) => s.tabOrientation === 'vertical');
  const lockDuration = useLockDuration(lockInfo, lockTimeoutMinutes);
  // Object Explorer pin — distinct from tab.isPinned/onPinTab (tab-strip pin, unrelated).
  const workspacePinState = useWorkspacePinState(tab.id);
  const workspacePinActions = useWorkspacePinActions(tab.id);
  const isWorkspacePinEligible =
    tab.type === 'query' ||
    tab.type === 'table-explorer' ||
    tab.type === 'notebook' ||
    // B5/F07 — a comparison pins under its SOURCE connection; the scope comes
    // from `pinScopeForTab`, not this tab's `connectionInfo`.
    tab.type === 'data-compare' ||
    tab.type === 'schema-compare';
  const isFileBackedQuery = isQueryTab(tab) && !!tab.content.filePath;
  const filePath = isQueryTab(tab) ? tab.content.filePath : isNotebookTab(tab) ? tab.content.filePath : undefined;
  const fileName = filePath ? getFileNameFromPath(filePath) : '';
  const baseTitle = filePath ? (fileName || tab.title) : tab.title;
  const TabTypeIcon = getTabIcon(tab);

  const canForceUnlock = (() => {
    if (!isAiLocked) return false;
    if (!lockInfo) return true;
    if (lockTimeoutMinutes <= 0) return true;

    const reference = lockInfo.lastActivity ?? lockInfo.lockedAt;
    const referenceMs = Date.parse(reference);
    if (!Number.isFinite(referenceMs)) return true;
    return Date.now() - referenceMs >= lockTimeoutMinutes * 60_000;
  })();
  const isFileDirty =
    isQueryTab(tab) && filePath
      ? isFileOutOfDate({ query: tab.content.query || '', fileLastSavedHash: tab.content.fileLastSavedHash })
      : isNotebookTab(tab) ? tab.content.isDirty
      : false;
  const isSourceModified = isPlSqlEditorTab(tab) && tab.content.sourceModified;
  const displayTitle = isFileDirty ? `* ${baseTitle}` : isSourceModified ? `● ${baseTitle}` : baseTitle;
  // PP-09 dirty dot (CH-16/17) — `--energy` per the accent system: unsaved
  // content is genuinely uncommitted work, same role as T3.5's transaction
  // dot. Kept ALONGSIDE the `*`/`●` text prefix above rather than replacing
  // it: that prefix is a pre-existing, tested (scriptBrowserDirtyAndRetention
  // .spec.ts), a11y-friendly text signal, and removing it isn't something
  // this task's brief asked for — the dot adds the "at a glance" accent
  // color signal the prefix never had (it inherits the title's plain text
  // color today).
  const isDirty = isFileDirty || isSourceModified;

  // Transaction state for query tabs
  const transactionMode: TransactionMode | undefined =
    isQueryTab(tab) ? tab.content.transactionMode : undefined;
  const transactionState: TransactionState | undefined =
    isQueryTab(tab) ? tab.content.transactionState : undefined;
  // G-5 (T6.1b): gated explicitly on mode, not derived from the toolbar's
  // `isEnergized` tint — those two are deliberately different conditions.
  // Auto-commit never leaves a lingering open transaction for the user to
  // act on, so this dot must never show there even if some producer reports
  // `active` while mode is still `auto` (verified reachable: the MCP
  // `transaction_begin` tool notifies the renderer with `transactionState:
  // 'active'` without itself checking the tab's mode, despite its own tool
  // description claiming manual/smart-only — see task-T6.1b-report.md).
  const hasActiveTransaction = transactionState === 'active' && transactionMode !== 'auto';

  // Build aria-label for screen readers
  const ariaLabel = useMemo(() => {
    const parts = [displayTitle];
    if (tab.isPinned) parts.push('pinned');
    if (isActive) parts.push('active');
    if (isDisconnected) parts.push('connection offline');
    if (isAiLocked) parts.push('AI is editing');
    return parts.join(', ');
  }, [displayTitle, tab.isPinned, isActive, isDisconnected, isAiLocked]);

  // Focus input when editing starts. Covers the direct-trigger paths
  // (double-click, and Rename with no menu involved) where nothing else is
  // fighting for focus. The Shift+F10/right-click menu paths need a second,
  // later focus call — see the `onCloseAutoFocus` handlers below.
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Update edit value when tab title changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditValue(tab.title);
    }
  }, [tab.title, isEditing]);

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSaveTitle(editValue);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditValue(tab.title);
      onStopEditing();
    }
  };

  // Handle keyboard events on the tab.
  //
  // CH-18 (T4.7) activation model — MANUAL activation: arrow keys move focus
  // only, Enter/Space activate. The plan text lists "arrows move" and "Enter
  // activates" as separate bullets, which already points at manual over
  // automatic activation, and it's the right call here independently of that
  // wording: activating a tab in this app mounts an editor/grid and can
  // trigger real work (query re-execution wiring, notebook mounts), so
  // arrowing across five tabs must not fire five activations. Space is
  // bound alongside Enter — not spelled out in the plan text, but WAI-ARIA's
  // manual-activation tabs pattern lists Space as a second activation key,
  // and leaving it unhandled here would regress to the browser's default
  // page-scroll-on-Space now that this element is a real keyboard target.
  //
  // Row-boundary / end-of-strip: Left/Right (Up/Down in vertical mode) walk
  // `[role="tab"]` elements in DOM order, not visual position. The strip
  // wraps to multiple visual rows in horizontal mode (a locked product
  // decision), but that wrap is a CSS flex-wrap artifact — the underlying
  // list is still one flat sequence, so crossing a row boundary is just
  // "the next element in DOM order" with no special-casing needed. Both ends
  // wrap around (last→first, first→last), matching the ARIA APG's
  // recommended tablist behavior and how native/VS-Code-style tab strips
  // behave. Up/Down are deliberately left UNBOUND in horizontal mode: a
  // wrapped multi-row strip has no consistent "tab above/below" — rows can
  // hold different numbers of variable-width tabs — so real 2-D navigation
  // would need geometry hit-testing for a path that's rarely used; ARIA APG
  // itself only binds arrows to a tablist's semantic axis (horizontal here),
  // not both axes just because it happens to wrap visually. Symmetrically,
  // vertical mode is a plain single-column list, so Up/Down are its natural
  // axis and Left/Right stay unbound.
  const handleTabKeyDown = (e: React.KeyboardEvent) => {
    if (e.defaultPrevented) return;
    // The rename `<input>` (isEditing) has its own onKeyDown, but this
    // handler is on the parent tab div and keydown bubbles — without this
    // guard, every keystroke while renaming would ALSO try to move focus or
    // activate a tab (e.g. Enter would both save the title AND activate).
    if (isEditing) return;

    // Ctrl/Cmd + Arrow to move tab within group (orientation-aware) — unchanged
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
      const prevKey = isVertical ? 'ArrowUp' : 'ArrowLeft';
      const nextKey = isVertical ? 'ArrowDown' : 'ArrowRight';
      if (e.key === prevKey) {
        e.preventDefault();
        onMoveTab('left');
        return;
      }
      if (e.key === nextKey) {
        e.preventDefault();
        onMoveTab('right');
        return;
      }
    }

    // Shift+F10 — the platform-standard keyboard route to a context menu,
    // and the only one for users without a right-click. Replaces the old
    // Enter/Space "open dropdown" wiring now that those keys activate.
    if (e.shiftKey && e.key === 'F10') {
      e.preventDefault();
      if (isStartPage) return; // no menu exists for the start page tab
      allowDropdownOpenRef.current = true;
      setDropdownOpen(true);
      return;
    }

    // Enter / Space activate the focused tab (manual activation — see the
    // design note above).
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate();
      return;
    }

    // Arrow keys move focus WITHOUT activating (manual activation).
    const forwardKey = isVertical ? 'ArrowDown' : 'ArrowRight';
    const backwardKey = isVertical ? 'ArrowUp' : 'ArrowLeft';
    if (e.key === forwardKey || e.key === backwardKey) {
      e.preventDefault();
      const strip = (e.currentTarget as HTMLElement).closest('[role="tablist"]');
      if (!strip) return;
      const allTabs = Array.from(strip.querySelectorAll<HTMLElement>('[role="tab"]'));
      const currentIndex = allTabs.indexOf(e.currentTarget as HTMLElement);
      if (currentIndex === -1) return;
      const delta = e.key === forwardKey ? 1 : -1;
      const nextIndex = (currentIndex + delta + allTabs.length) % allTabs.length;
      allTabs[nextIndex]?.focus();
    }
  };

  const handleDropdownOpenChange = (open: boolean) => {
    if (open) {
      // Prevent accidental open on click; allow only when requested via keyboard.
      if (allowDropdownOpenRef.current) {
        setDropdownOpen(true);
      }
      return;
    }

    allowDropdownOpenRef.current = false;
    setDropdownOpen(false);
  };

  // T6.1c (item 11) — A LATENT COUPLING, recorded rather than tested.
  //
  // The tab strip's single-tab-stop invariant (CH-18/T4.7) is guaranteed
  // TODAY by this function, not by the rename input's `tabIndex={-1}`: this
  // closes an open rename on ANY blur, so "rename open on tab B while focus
  // sits on tab A" is unreachable BY CONSTRUCTION. A positive test for that
  // state would pass vacuously — it would assert one tab stop in a world where
  // two can never exist — and an earlier round correctly removed one.
  //
  // The `tabIndex={-1}` stays. It is correct defence-in-depth and costs
  // nothing. THE RISK IS FUTURE: if this function ever gains a guard — the
  // obvious one being "don't close when the blur target is inside the tab", to
  // stop a rename collapsing when the user clicks its own confirm button —
  // then two tab stops become reachable, `tabIndex={-1}` becomes load-bearing
  // rather than belt-and-braces, and the positive test becomes worth writing.
  // If you are here adding that guard: write it then.
  const handleBlur = () => {
    if (editValue.trim() !== tab.title) {
      onSaveTitle(editValue);
    } else {
      onStopEditing();
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isStartPage || isFileBackedQuery) return;
    onStartEditing();
  };

  const isDragging = sortableProps?.isDragging ?? false;

	  // PP-09 connection bar (3px→2px, inactive slightly transparent) — shared
	  // with the drag-overlay TabItemContent via connectionBarStyle() above.
	  const tabColorStyle: React.CSSProperties | undefined = connectionColor
	    ? { ...connectionBarStyle(connectionColor, isActive, isVertical), ...(sortableProps?.style || {}) }
	    : sortableProps?.style;

	  // PP-09 hover wash: `hover:bg-muted/60` is only added on the INACTIVE
	  // branches below. The ACTIVE branches instead PIN hover to the same
	  // `bg-background` they already carry (`hover:bg-background`) — mirroring
	  // TableExplorerGrid.tsx's pin pattern (preferred here because a tab must
	  // stay fully interactive/hoverable regardless of active state). Tailwind
	  // emits hover-variant rules after plain utilities, so an UNCONDITIONAL
	  // `hover:bg-muted/60` would outrank `bg-background` on hover regardless
	  // of class order — this wave's recurring "hover silently outranks a
	  // state class" defect, hit 4 times already; the pin avoids a 5th.
	  const tabContent = (
	    <div
	      ref={sortableProps?.ref}
	      style={tabColorStyle}
	      {...(sortableProps?.attributes as React.HTMLAttributes<HTMLDivElement>)}
	      {...(sortableProps?.listeners as React.HTMLAttributes<HTMLDivElement>)}
	      className={`
	        group flex items-center px-3 py-1.5 cursor-pointer select-none ${isVertical ? '' : '-mb-px'}
	        outline-none focus-visible:ring-2 focus-visible:ring-ring
	        ${connectionColor ? '' : isVertical ? 'border-l-2' : 'border-b-2'}
        ${
          isActive
            ? connectionColor
              ? 'bg-background hover:bg-background text-foreground'
              : isVertical
                ? 'bg-background hover:bg-background text-foreground border-l-primary'
                : 'bg-background hover:bg-background text-foreground border-b-primary'
            : connectionColor
              ? 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
              : isVertical
                ? 'text-muted-foreground hover:text-foreground hover:bg-muted/60 border-l-transparent'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/60 border-b-transparent'
        }
        ${isDragging ? 'opacity-50 ring-2 ring-primary/50' : ''}
        ${extraClassName ?? ''}
	      `}
	      role="tab"
	      // CH-18 (T4.7) roving tabindex: exactly one tab (the active one) is
	      // a real Tab stop; every other tab is -1. Interactive children (the
	      // close button, the AI-lock abort button below) also carry -1 so
	      // the whole strip stays a SINGLE stop — reached via arrow keys
	      // (handleTabKeyDown) and Shift+F10/middle-click/the context menu,
	      // not via further Tab presses.
	      tabIndex={isActive ? 0 : -1}
	      aria-selected={isActive}
	      aria-label={ariaLabel}
	      onClick={onActivate}
	      onDoubleClick={handleDoubleClick}
	      onKeyDown={handleTabKeyDown}
	      onMouseDown={(e) => {
	        // Suppress Chromium's middle-click autoscroll cursor so it
	        // doesn't fight the close action below.
	        if (e.button === 1) e.preventDefault();
	      }}
	      onAuxClick={(e) => {
	        // `click` only fires for the primary (left) button; `auxclick` is
	        // the DOM event for middle/secondary buttons. Start page has no
	        // close affordance at all (see the `!isStartPage` close button
	        // below), so middle-click doesn't close it either.
	        if (e.button === 1 && !isStartPage) {
	          e.preventDefault();
	          onClose();
	        }
	      }}
	      data-testid={`tab-${tab.id}`}
	      data-disconnected={isDisconnected ? 'true' : undefined}
	      data-dragging={isDragging ? 'true' : undefined}
	    >
      {/* Tab type icon */}
      <TabTypeIcon className="h-3.5 w-3.5 flex-shrink-0 mr-1.5 text-primary/70" />
      {isEditing ? (
        // CH-18 (T4.7) fix round 1: `tabIndex={-1}` — same reasoning as the
        // close button and the AI-lock abort button above. Without it, this
        // input defaults to a real Tab stop (native <input> behavior), so
        // renaming an INACTIVE tab (arrow-focus it, Shift+F10 → Rename) put
        // TWO simultaneous tab stops in the strip: this input and whichever
        // tab is still active elsewhere. `tabIndex={-1}` doesn't affect
        // programmatic focus — the `useEffect` above still calls
        // `inputRef.current.focus()` on mount exactly as before.
        <input
          ref={inputRef}
          type="text"
          tabIndex={-1}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onBlur={handleBlur}
          className="bg-transparent border-b border-primary outline-none w-24 text-xs"
          onClick={(e) => e.stopPropagation()}
          data-testid={`tab-edit-input-${tab.id}`}
        />
      ) : (
        <>
          <span
            className={`truncate min-w-0 ${isVertical ? 'flex-1' : ''}`}
            style={!isVertical && titleMaxWidthPx ? { maxWidth: `${titleMaxWidthPx}px` } : undefined}
            title={filePath ? filePath : tab.title}
            data-testid={`tab-title-${tab.id}`}
          >
            {displayTitle}
            {tab.customTitle && !isFileBackedQuery && (
              <span className="text-muted-foreground opacity-60">*</span>
            )}
          </span>
          {(isDisconnected || isAiLocked || tab.isPinned || hasActiveTransaction || isDirty) && (
            <span className="flex items-center gap-1 flex-shrink-0 ml-1">
              {isDisconnected && (
                // G-4 (T6.1b): this used to be the AlertTriangle PLUS a
                // separate amber dot beside it — same "connection offline"
                // meaning on two channels, in a hue adjacent to the two
                // genuine --energy dots in this same cluster. The dot
                // carried no information the triangle didn't already say
                // unambiguously (it has its own tooltip), so it was
                // retired rather than just retoned. A dead connection is a
                // fault, not "work in flight", so the surviving glyph moved
                // onto `text-destructive` instead of staying amber.
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex-shrink-0">
                      <AlertTriangle
                        className="h-3 w-3 text-destructive"
                        data-testid={`tab-disconnected-${tab.id}`}
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    Connection offline
                  </TooltipContent>
                </Tooltip>
              )}
              {isAiLocked && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="flex-shrink-0 hover:bg-red-200 dark:hover:bg-red-900/50 rounded p-0.5 transition-colors"
                      // T6.1c (item 2) — a glyph-only button with no text and
                      // no accessible name is invisible to a screen reader:
                      // it announced as just "button". T4.7 gave the ✕ and the
                      // menu their names but its brief named only those two,
                      // so this one was flagged rather than fixed. The label
                      // is stateful because the button is: once an abort is
                      // requested it goes `disabled`, and a name that still
                      // said "stop the edit" would be a lie.
                      // The hardcoded `hover:bg-red-200 dark:hover:bg-red-900/50`
                      // above is deliberately LEFT ALONE — it was explicitly
                      // recorded for the final review and is out of scope for
                      // both T6.1b and T6.1c. Not an oversight.
                      aria-label={
                        lockInfo?.abortRequested
                          ? 'Stopping the AI edit…'
                          : 'AI is editing this tab — stop the edit'
                      }
                      // CH-18 (T4.7): excluded from the normal Tab order, same
                      // reasoning as the close button below — the roving tab
                      // strip is one Tab stop, not one-plus-however-many
                      // nested buttons happen to be visible on the active tab.
                      tabIndex={-1}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (lockInfo?.abortRequested) return;
                        onAbortEdit();
                      }}
                      data-testid={`tab-ai-locked-${tab.id}`}
                      disabled={lockInfo?.abortRequested}
                    >
                      <Bot
                        className={`h-3 w-3 ${
                          lockInfo?.abortRequested
                            ? 'text-warning'
                            : lockDuration?.isStale
                              ? 'text-purple-500'
                              : 'text-green-600 animate-pulse'
                        }`}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    {lockInfo?.abortRequested
                      ? `Abort requested (${lockDuration?.formattedDuration ?? '...'})`
                      : lockDuration?.isStale
                        ? `AI editing stale (${lockDuration.formattedDuration}) - Click to abort`
                        : `AI editing${lockInfo?.message ? `: ${lockInfo.message}` : ''} (${lockDuration?.formattedDuration ?? '...'}) - Click to abort`}
                  </TooltipContent>
                </Tooltip>
              )}
              {tab.isPinned && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex-shrink-0">
                      <Pin
                        className="h-3 w-3 text-blue-500"
                        data-testid={`tab-pinned-${tab.id}`}
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    Pinned tab
                  </TooltipContent>
                </Tooltip>
              )}
              {hasActiveTransaction && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex-shrink-0" data-testid={`tab-transaction-${tab.id}`}>
                      {/* G-4 (T6.1b): PP-09 made this dot byte-identical to
                          the dirty dot below, on the reasoning that both are
                          --energy and should read consistently. Both being
                          --energy is still right (locked accent system), but
                          identical rendering meant the both-at-once case
                          showed two indistinguishable dots — a quantity, not
                          two states. Solid fill is the heavier of this
                          cluster's two energy dots: an open transaction
                          holds locks and has uncommitted writes riding on
                          it, a real cost to closing the tab, unlike the
                          dirty dot's "you'd lose unsaved text". `animate-
                          pulse` is the one treatment this task picked for
                          "genuinely open" across all three transaction
                          surfaces (tab / toolbar / status bar) — see
                          TransactionToolbarControls.tsx and
                          QueryStatusBar.tsx. */}
                      <span className="inline-block h-2 w-2 rounded-full bg-energy animate-pulse" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    Active transaction
                  </TooltipContent>
                </Tooltip>
              )}
              {isDirty && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex-shrink-0" data-testid={`tab-dirty-${tab.id}`}>
                      {/* G-4 (T6.1b): hollow ring, not a second solid dot —
                          see the transaction dot above for the heavier
                          member of this --energy pair and why. Unsaved text
                          is real but reversible with no engine-side
                          consequence, so it stays static (no pulse) and
                          outline-only; this is the "cheapest distinction
                          that survives both themes" the task brief called
                          for, verified at real tab scale in both themes. */}
                      <span
                        className="inline-block h-2 w-2 rounded-full border border-energy"
                        aria-label="Unsaved changes"
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    Unsaved changes
                  </TooltipContent>
                </Tooltip>
              )}
            </span>
          )}
        </>
      )}
      {!isStartPage && (
        // PP-09: unconditionally mounted (never conditionally rendered) so
        // its box is ALWAYS reserved — toggling opacity can never shift the
        // title/indicators before it. Visible on hover/focus or when this
        // tab is active; hidden (opacity-0, still occupying its box)
        // otherwise. `aria-label` added because this was an icon-only button
        // with no accessible name at all — T4.7 (tab keyboard semantics)
        // inherits a real <button> with a name to build on rather than
        // having to add one from scratch.
        //
        // CH-18 (T4.7): `tabIndex={-1}` — not a normal Tab stop, so it can't
        // break "the roving tab strip is a single Tab stop" (req #1). It's
        // reached via Shift+F10 → Close, or middle-click, not by tabbing to
        // it. That means "visible on hover" alone would be a dead end for a
        // keyboard user: they can arrow-focus an INACTIVE tab, but its ✕
        // only had `group-hover:opacity-100` before, so it stayed invisible
        // the whole time they had it focused. `group-focus-visible` (the
        // PARENT tab's `:focus-visible`, via the `group` class on that div)
        // reveals it the same way hover does — the button's own
        // `focus-visible:opacity-100` is kept too, harmless, in case
        // something ever calls `.focus()` on it directly.
        <button
          type="button"
          tabIndex={-1}
          className={`ml-1.5 p-0.5 rounded-sm hover:bg-muted focus-visible:opacity-100 group-hover:opacity-100 group-focus-visible:opacity-100 ${
            isActive ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close tab"
          data-testid={`tab-close-${tab.id}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );

  // Render menu items (shared between context menu and dropdown menu)
  const menuItems = (MenuItem: typeof ContextMenuItem | typeof DropdownMenuItem, MenuSeparator: typeof ContextMenuSeparator | typeof DropdownMenuSeparator) => (
    <>
      {!isFileBackedQuery && (
        <MenuItem
          onClick={() => {
            suppressMenuCloseAutoFocusRef.current = true;
            onStartEditing();
          }}
        >
          <Pencil className="mr-2 h-3.5 w-3.5" />
          <span>Rename</span>
        </MenuItem>
      )}
      {tab.customTitle && !isFileBackedQuery && (
        <MenuItem onClick={onResetToAuto}>
          <RotateCcw className="mr-2 h-3.5 w-3.5" />
          <span>Reset to auto</span>
        </MenuItem>
      )}
      {isAiLocked && !lockInfo?.abortRequested && (
        <MenuItem onClick={onAbortEdit} className="text-red-600">
          <X className="mr-2 h-3.5 w-3.5" />
          <span>Abort AI editing</span>
        </MenuItem>
      )}
      {isAiLocked && canForceUnlock && (
        <MenuItem onClick={onForceUnlock} className="text-warning">
          <Unlock className="mr-2 h-3.5 w-3.5" />
          <span>Force unlock</span>
        </MenuItem>
      )}
      <MenuSeparator />
      {tab.isPinned ? (
        <MenuItem onClick={onUnpinTab}>
          <PinOff className="mr-2 h-3.5 w-3.5" />
          <span>Unpin tab</span>
        </MenuItem>
      ) : (
        <MenuItem onClick={onPinTab}>
          <Pin className="mr-2 h-3.5 w-3.5" />
          <span>Pin tab</span>
        </MenuItem>
      )}
      {isWorkspacePinEligible && (
        <>
          <MenuSeparator />
          {workspacePinState.mode === 'disabled' ? (
            // Repo convention (feedback_vqe_disabled_buttons_need_tooltip) —
            // mirrors TreeNodeContextMenu.tsx's disabled-item pattern. Radix's
            // own `disabled` prop applies `pointer-events-none`, which also
            // blocks hover (and therefore a native `title` tooltip) — so this
            // item stays pointer-events-enabled, is styled muted manually, and
            // guards `onSelect` instead of relying on Radix's disabled state.
            <MenuItem
              onSelect={(e) => e.preventDefault()}
              title={workspacePinState.reason}
              aria-disabled="true"
              className="cursor-not-allowed opacity-50 hover:bg-transparent hover:text-foreground focus:bg-transparent"
              data-disabled-reason={workspacePinState.reason}
            >
              <LayoutPanelLeft className="mr-2 h-3.5 w-3.5" />
              <span>Pin to Object Explorer</span>
            </MenuItem>
          ) : workspacePinState.mode === 'linked' ? (
            <MenuItem onClick={() => workspacePinActions.unpin(workspacePinState.pin)}>
              <LayoutPanelLeft className="mr-2 h-3.5 w-3.5" />
              <span>Unpin from Object Explorer</span>
            </MenuItem>
          ) : (
            <MenuItem onClick={workspacePinActions.createPin}>
              <LayoutPanelLeft className="mr-2 h-3.5 w-3.5" />
              <span>Pin to Object Explorer</span>
            </MenuItem>
          )}
        </>
      )}
      <MenuSeparator />
      <MenuItem onClick={onClose} className="text-destructive">
        <X className="mr-2 h-3.5 w-3.5" />
        <span>Close</span>
      </MenuItem>
      <MenuItem
        onClick={onCloseOtherTabs}
        disabled={!canCloseOtherTabs}
        className={!canCloseOtherTabs ? 'opacity-50' : ''}
      >
        <XCircle className="mr-2 h-3.5 w-3.5" />
        <span>Close others</span>
      </MenuItem>
      <MenuItem
        onClick={onCloseTabsToRight}
        disabled={!canCloseTabsToRight}
        className={!canCloseTabsToRight ? 'opacity-50' : ''}
      >
        <ArrowRightToLine className="mr-2 h-3.5 w-3.5" />
        <span>Close tabs to the right</span>
      </MenuItem>
      <MenuItem
        onClick={onCloseUnpinnedTabs}
        disabled={!canCloseUnpinnedTabs}
        className={!canCloseUnpinnedTabs ? 'opacity-50' : ''}
      >
        <ListX className="mr-2 h-3.5 w-3.5" />
        <span>Close besides pinned</span>
      </MenuItem>
    </>
  );

  // Wrap with both context menu (for right-click) and dropdown menu (for keyboard)
  if (isStartPage) {
    return tabContent;
  }

  // CH-18 (T4.7) fix round 1, Finding 1 (continued) — this is the second half
  // of the fix, and the one that actually makes the rename input receive
  // focus. The `isEditing` effect above calls `inputRef.current.focus()` in
  // the SAME commit that closes the menu, but at that exact moment the
  // menu's Radix `FocusScope` is still trapped (its exit animation hasn't
  // finished, so the scope hasn't torn down yet) and silently steals focus
  // straight back — verified by instrumenting the effect: the `.focus()`
  // call returned with `document.activeElement` still a menu-related `DIV`,
  // never the input, not even momentarily. `onCloseAutoFocus` fires later —
  // once the FocusScope has actually released, post-animation — so re-issuing
  // `.focus()` here is the call that actually wins.
  const handleMenuCloseAutoFocus = (e: Event) => {
    if (!suppressMenuCloseAutoFocusRef.current) return;
    e.preventDefault();
    suppressMenuCloseAutoFocusRef.current = false;
    inputRef.current?.focus();
    inputRef.current?.select();
  };

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={handleDropdownOpenChange}>
      <ContextMenu>
        <DropdownMenuTrigger asChild>
          <ContextMenuTrigger asChild>{tabContent}</ContextMenuTrigger>
        </DropdownMenuTrigger>
        <ContextMenuContent className="w-40" onCloseAutoFocus={handleMenuCloseAutoFocus}>
          {menuItems(ContextMenuItem, ContextMenuSeparator)}
        </ContextMenuContent>
      </ContextMenu>
      <DropdownMenuContent
        className="w-40"
        data-testid={`tab-dropdown-${tab.id}`}
        onCloseAutoFocus={handleMenuCloseAutoFocus}
      >
        {menuItems(DropdownMenuItem, DropdownMenuSeparator)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
